import AnchorCalculator from './AnchorCalculator.js';
// AnchorCalculator, ShapeGeoBuilder, and umlConst are all re-exported in the
// @jxUml barrel (UmlWorkspace/index.js) together with ConnectorBuilder itself
// — import by relative path here to avoid a self-referential cycle through
// that barrel.
import ShapeGeoBuilder from '../../Types/ShapeGeoBuilder.js';
import umlConst from '../../Types/UmlConsts.js';

/**
 * Connector geometry: path/edges/style/svg-d/midpoint for a UmlConnector.
 * Stateless, static methods only.
 */
class ConnectorBuilder {
    /**
     * Resolves the connector's visual style, falling back to a default per kind when not explicitly set.
     * @param {object} connector - connector data object
     * @returns {object} style object with dash/arrowHead/color/strokeWidth
     */
    static style(connector) {
        return connector.style || umlConst.CONNECTOR_STYLES[connector.kind] || umlConst.CONNECTOR_STYLES.association;
    }

    /**
     * Builds the ordered point list for a connector's path: start, waypoints, end.
     * @param {object} connector - connector data
     * @param {object} fromShape - source shape
     * @param {object} toShape - target shape
     * @returns {Array<{x:number,y:number}>} path points
     */
    static points(connector, fromShape, toShape) {
        const waypoints = connector.points;
        // No waypoints AND both anchors auto (null) -> use a SINGLE shared X (vertical)
        // or shared Y (horizontal) for both ends, computed once from the two shape
        // centers. Each end's edge is otherwise resolved independently toward the
        // OTHER shape's raw corner, which can land the two ends on different X
        // (vertical case) or Y (horizontal case) when the shapes aren't perfectly
        // aligned, producing a visibly diagonal line for what should read as a
        // straight vertical/horizontal connector.
        if (waypoints.length === 0 && !connector.fromAnchorPoint && !connector.toAnchorPoint) {
            const { start, end } = ConnectorBuilder.autoEdges(fromShape, toShape);
            return [start, end];
        }
        const firstTarget = waypoints.length > 0 ? waypoints[0] : toShape;
        const lastSource = waypoints.length > 0 ? waypoints[waypoints.length - 1] : fromShape;
        const start = connector.fromAnchorPoint
            ? AnchorCalculator.toShape(fromShape, connector.fromAnchorPoint)
            : AnchorCalculator.edgeToward(fromShape, firstTarget.x, firstTarget.y);
        const end = connector.toAnchorPoint
            ? AnchorCalculator.toShape(toShape, connector.toAnchorPoint)
            : AnchorCalculator.edgeToward(toShape, lastSource.x, lastSource.y);
        return [
            start,
            ...waypoints.map(function toXY(point) {
                return { x: point.x, y: point.y };
            }),
            end,
        ];
    }

    /**
     * Auto-edge points for a connector with no waypoints and no manual anchors —
     * shares ONE X (if the connection reads as vertical) or ONE Y (if horizontal)
     * between both ends, instead of each end picking its own coordinate toward
     * the other shape's corner. Keeps the line a clean vertical/horizontal
     * segment even when the two shapes' centers aren't perfectly aligned.
     * @param {object} fromShape
     * @param {object} toShape
     * @returns {{start:{x:number,y:number}, end:{x:number,y:number}}}
     */
    static autoEdges(fromShape, toShape) {
        const fromH = ShapeGeoBuilder.effectiveHeight(fromShape);
        const toH = ShapeGeoBuilder.effectiveHeight(toShape);
        const fromCenter = { x: fromShape.x + fromShape.w / 2, y: fromShape.y + fromH / 2 };
        const toCenter = { x: toShape.x + toShape.w / 2, y: toShape.y + toH / 2 };
        const dx = toCenter.x - fromCenter.x;
        const dy = toCenter.y - fromCenter.y;
        const isVerticalDominant = Math.abs(dy) >= Math.abs(dx);

        if (isVerticalDominant) {
            const sharedX = fromCenter.x;
            return {
                start: {
                    x: AnchorCalculator.clamp(sharedX, fromShape.x + umlConst.ANCHOR_EDGE_MARGIN, fromShape.x + fromShape.w - umlConst.ANCHOR_EDGE_MARGIN),
                    y: dy > 0 ? fromShape.y + fromH : fromShape.y,
                },
                end: {
                    x: AnchorCalculator.clamp(sharedX, toShape.x + umlConst.ANCHOR_EDGE_MARGIN, toShape.x + toShape.w - umlConst.ANCHOR_EDGE_MARGIN),
                    y: dy > 0 ? toShape.y : toShape.y + toH,
                },
            };
        }

        const sharedY = fromCenter.y;
        return {
            start: {
                x: dx > 0 ? fromShape.x + fromShape.w : fromShape.x,
                y: AnchorCalculator.clamp(sharedY, fromShape.y + umlConst.ANCHOR_EDGE_MARGIN, fromShape.y + fromH - umlConst.ANCHOR_EDGE_MARGIN),
            },
            end: {
                x: dx > 0 ? toShape.x : toShape.x + toShape.w,
                y: AnchorCalculator.clamp(sharedY, toShape.y + umlConst.ANCHOR_EDGE_MARGIN, toShape.y + toH - umlConst.ANCHOR_EDGE_MARGIN),
            },
        };
    }

    static svgPath(points) {
        return points
            .map(function toSegment(point, index) {
                return `${index === 0 ? 'M' : 'L'}${point.x},${point.y}`;
            })
            .join(' ');
    }

    /**
     * Centroid of all path points — a simple, stable approximation of the line's midpoint, used to anchor the label.
     * @param {Array<{x:number,y:number}>} points - path points
     * @returns {{x:number,y:number}} midpoint
     */
    static midpoint(points) {
        const sum = points.reduce(
            function addPoint(acc, point) {
                return { x: acc.x + point.x, y: acc.y + point.y };
            },
            { x: 0, y: 0 },
        );
        return { x: sum.x / points.length, y: sum.y / points.length };
    }
}

export default ConnectorBuilder;
